import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { getCSRFToken, displayableUrl } from "./utils/various";
import {
  Company,
  Entity,
  ChannelAccount,
  ProvisionalBody,
} from "../types";
import BodyLink, { getCtaOptions } from "./BodyLinks";
import {
  FaceSmileIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  CloudArrowDownIcon,
  SparklesIcon,
  XMarkIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import useChatGPT from "../hook/useChatGpt";
import useAiImageGeneration from "../hook/useAiImageGeneration";
import EmojiPickerCustom from "./EmojiPickerCustom";
import Modal from "./Modal";
import CustomCloseButton from "./CustomCloseButton";
import ModalInput from "./ModalInput";
import Tooltip from "./Tooltip";
import { GoRepoForked } from "react-icons/go";
import CustomPopover from "./Popover";
import { useTextStyles } from "../hook/useTextStyles";
import { useMentionsAndVariables } from "./utils/publisher/utils";
import VariableSelector from "./Publisher/variables/VariableSelector";
import CharacterCounter from "./CharacterCounter";

type Props = {
  index: number;
  isActive: boolean;
  isLast: boolean;
  body: ProvisionalBody;
  setHBodies: React.Dispatch<
    React.SetStateAction<Record<number, ProvisionalBody>>
  >;
  // Génération d'images par IA autorisée par le groupe (Company#ai_image_enabled?)
  aiImageEnabled?: boolean;
  onRemoval?: (bPid: ProvisionalBody["provisional_id"]) => void;
  onDuplication?: (bPid: ProvisionalBody["provisional_id"]) => void;
  targetsSelectorRenderer: (
    bPid: ProvisionalBody["provisional_id"],
    buttonUI?: JSX.Element,
  ) => JSX.Element;
  mentionSelectorRenderer?: (
    body: ProvisionalBody,
    insert: Function,
  ) => JSX.Element;
  mentions?: { [k: string]: Entity };
  variables?: Record<string, string | null>;
  /** Le dispenser dont on édite un contenu : porteur des compositions créées ici. */
  contentDispenserId?: number;
  characterLimit?: number;
  hChannelAccounts?: Record<number, ChannelAccount>;
  /** Nombre d'AUTRES versions qui pointent la même composition (visuel partagé). */
  sharedWithCount?: number;
};

export const BodyEditor = memo(
  ({
    body,
    aiImageEnabled = true,
    setHBodies,
    onDuplication,
    onRemoval,
    isActive,
    isLast,
    targetsSelectorRenderer,
    index,
    mentionSelectorRenderer,
    mentions,
    variables,
    contentDispenserId,
    characterLimit,
    hChannelAccounts,
    sharedWithCount = 0,
  }: Props) => {
    const intl = useIntl();
    const updateBody = (data: Object) => {
      const pid = body.provisional_id;
      setHBodies((prev) => ({ ...prev, [pid]: { ...prev[pid], ...data } }));
    };

    // La nouvelle version GARDE le composition_id (visuel partagé entre versions, plan §6.8)
    // : les deux îlots PATCHent la même compo, comme l'ancien publisher partageait ses médias.
    // Diverger est un geste (divergeComposition). Seul l'id du body est écarté.
    const duplicateBody = ({ id, ...body }: ProvisionalBody) => {
      let newBodyPid: number | null = null;
      setHBodies((prev) => {
        newBodyPid =
          Math.max(...Object.keys(prev).map((k) => parseInt(k)), 0) + 1;
        return {
          ...prev,
          [newBodyPid]: {
            ...body,
            message: `${body.message || ""}`,
            provisional_id: newBodyPid,
          },
        };
      });
      // je reconnais que c'est tordu mais comme ça on a accès à la valeur
      setTimeout(() => {
        if (onDuplication && newBodyPid) {
          onDuplication(newBodyPid);
        }
      }, 0);
    };

    // « Personnaliser pour cette version » : cette version quitte le visuel partagé pour sa
    // propre copie (brouillon orphelin servi par le serveur, adopté au save). Les autres
    // versions gardent la compo commune.
    const divergeComposition = async () => {
      const { composition_id, provisional_id: pid } = body;
      if (!composition_id) return;

      const response = await fetch(`/compositions/${composition_id}/fork`, {
        method: "POST",
        headers: { "X-CSRF-Token": getCSRFToken() },
      });
      if (!response.ok) return;

      const { id: forkId } = await response.json();
      setHBodies((prev) =>
        prev[pid] ? { ...prev, [pid]: { ...prev[pid], composition_id: forkId } } : prev,
      );
    };

    const deleteBody = (body: ProvisionalBody) => {
      onRemoval && onRemoval(body.provisional_id);
      // On remove à l'assignation
      setHBodies(({ [body.provisional_id]: _, ...keep }) => keep);
    };

    // L'image IA ou celle du smartlink fait une composition NEUVE : le body bascule dessus
    // (orpheline, adoptée au save comme un fork) — l'îlot suit son nouveau composition_id.
    const switchComposition = (compositionId: number) => updateBody({ composition_id: compositionId });

    // TextArea Buttons
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fakeInputRef = useRef<HTMLDivElement>(null);

    const insert = (inserted: string, inserted_length: number = 0) => {
      inserted_length = inserted_length || inserted.length; // handle mentions (inserted length is smaller than visible length)
      if (inputRef?.current) {
        const { selectionStart, selectionEnd, value } = inputRef.current;
        if (
          typeof selectionStart === "number" &&
          typeof selectionEnd === "number"
        ) {
          // typeOf car 0 est compté comme false
          const newValue = (inputRef.current.value = `${value.slice(
            0,
            selectionStart,
          )}${inserted}${value.slice(selectionEnd)}`);

          setHBodies((prev) => ({
            ...prev,
            [body.provisional_id]: {
              ...body,
              message: normalizeMentionsAndVariables(newValue),
            },
          }));
          inputRef?.current?.focus();
          setTimeout(() => {
            // Visiblement nécessaire
            if (inputRef.current) {
              inputRef.current.selectionStart =
                selectionStart + inserted_length;
              inputRef.current.selectionEnd = selectionStart + inserted_length;
            }
          }, 200);
        }
      }
    };

    const [emojiSelectorOpen, setEmojiSelectorOpen] = useState(false);
    const onEmojiSelect = (e: any) => {
      insert(String.fromCodePoint(parseInt(e.unified, 16)));
      setEmojiSelectorOpen(false);
    };
    // AI
    const [prompt, setPrompt] = useState("");
    // Modale IA unifiée : un onglet bascule entre génération de texte et d'image.
    const [showAiModal, setShowAiModal] = useState<boolean>(false);
    const [aiTab, setAiTab] = useState<"text" | "image">("text");
    const {
      error: gptError,
      fetchChatGPTResponse,
      isWriting: gptIsWriting,
      response: gptResponse,
    } = useChatGPT({
      apiRoute: "/bodies/openai_call",
    });

    // AI image (nano banana / Gemini) — génération asynchrone + polling
    const [imagePrompt, setImagePrompt] = useState("");

    const {
      generate: generateAiImage,
      loading: aiImageLoading,
      error: aiImageError,
    } = useAiImageGeneration({
      onStarted: () => {
        setShowAiModal(false);
        setImagePrompt("");
      },
      onProgress: () => {},
      // L'image produite devient la composition du body.
      onComplete: async (_jobId, cm) => {
        const response = await fetch("/compositions", {
          method: "POST",
          headers: { "X-CSRF-Token": getCSRFToken(), "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ content_medium_ids: [cm.id], content_dispenser_id: contentDispenserId }),
        });
        if (!response.ok) return;
        const { id } = await response.json();
        switchComposition(id);
      },
      onError: () => {
        setAiTab("image");
        setShowAiModal(true);
      },
    });

    // smartlink
    const [showSmartLinkModal, setShowSmartLinkModal] = useState(false);
    const [smartLinkUrl, setSmartLinkUrl] = useState("");
    const [smartLinkLoading, setSmartLinkLoading] = useState(false);
    const [smartLinkError, setSmartLinkError] = useState<string | null>(null);
    const toggleSmartlinkModal = () => {
      setShowSmartLinkModal(!showSmartLinkModal);
      setSmartLinkError(null);
      setSmartLinkUrl("");
    };

    // Le texte de ChatGPT remplace entièrement le message du body : l'ancien
    // texte est envoyé à l'IA comme contexte, il n'est pas conservé dans l'éditeur.
    useEffect(() => {
      if (!gptResponse?.text) {
        return;
      }
      if (showAiModal) {
        setShowAiModal(false);
      } // on ferme la modale pour voir le beau texte s'écrire
      setHBodies((prev) => ({
        ...prev,
        [body.provisional_id]: {
          ...body,
          message: gptResponse.text,
        },
      }));
    }, [gptResponse]);

    // Links
    const removeLink = () =>
      updateBody({ link_url: "", link_description: "", link_cta: "" });

    const replaceBodyWithRemote = async () => {
      setSmartLinkLoading(true);
      const creator = globalThis.location.href.includes("content_dispensers")
        ? {
          source: "content_dispenser",
          id: globalThis.location.href
            .split("content_dispensers/")[1]
            .split("/")[0],
        }
        : { source: "current_company" };
      const response = await fetch("/bodies/generate_from_url", {
        headers: {
          "X-CSRF-Token": getCSRFToken(),
          "Content-Type": "application/json",
        },
        method: "post",
        body: JSON.stringify({ url: smartLinkUrl, creator }),
      });
      const pid = body.provisional_id;
      if (!response.ok) {
        setSmartLinkError(
          intl.formatMessage({
            id: "body_editor.smartlink_error",
            defaultMessage: "Nous n'avons pas réussi à récupérer ce contenu",
          }),
        );
      } else {
        // Le texte, le lien et son titre ; l'image de la page arrive en composition neuve.
        const { composition_id, ...fields } = await response.json();
        updateBody(fields);
        if (composition_id) switchComposition(composition_id);
        setTimeout(() => setShowSmartLinkModal(false), 500);
      }
      setSmartLinkLoading(false);
    };

    const onChangeTextArea = (newValue: string) => {
      setHBodies((prev) => ({
        ...prev,
        [body.provisional_id]: { ...body, message: newValue },
      }));
    };

    const { applySelectedStyle } = useTextStyles(
      inputRef,
      body.message || "",
      onChangeTextArea,
    );

    const bodyCta = useMemo(
      () => getCtaOptions(intl).filter((c) => c.value === body?.link_cta)[0],
      [body?.link_cta, intl],
    );

    const updatePostType = (value: "post" | "post_and_story" | "story") => {
      setHBodies((prevBodies) => ({
        ...prevBodies,
        [body.provisional_id]: {
          ...prevBodies[body.provisional_id],
          post_type: value,
        },
      }));
    };
    // Handling Body for Mentions and Variables
    const {
      normalizeMentionsAndVariables,
      generateMentionOrVariableSpans,
      displayMentionOrVariableNames,
    } = useMentionsAndVariables(mentions, variables || {}, intl);

    const [provisionalMessage, setProvisionalMessage] = useState(body.message);

    // On force la mise à jour du textarea
    useEffect(() => {
      setProvisionalMessage(displayMentionOrVariableNames(body.message));
    }, [body.message]);
    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setProvisionalMessage(displayMentionOrVariableNames(e.target.value));

      setHBodies((prev) => ({
        ...prev,
        [body.provisional_id]: {
          ...body,
          message: normalizeMentionsAndVariables(e.target.value),
        },
      }));
    };

    const isInsta =
      !hChannelAccounts ||
      Object.values(hChannelAccounts).some(
        (account) =>
          account.channel?.identifier === "instagram" ||
          account.channel?.identifier === "facebook"
      );
    return (
      <div
        key={`body_provisional_id_${body.provisional_id}_${index}`}
        className={`flex relative z-0 ${!isActive && "lg:hidden"
          } flex-col border rounded-b-md rounded-tr-md px-2 py-4 md:px-4 md:py-4 relative bg-white z-0`}
      >
        <div id="header" className="flex justify-between w-full gap-3">
          <div className="flex lg:hidden">
            {targetsSelectorRenderer(body.provisional_id)}
          </div>

          <div className="flex flex-row-reverse flex-wrap items-center justify-between md:justify-between w-full gap-3">
            <div className="flex">
              {/* Options for mobile */}
              <CustomPopover
                position="left"
                disabled={false}
                button={
                  <div className="p-0 btn-neutral lg:hidden h-fit">
                    <EllipsisVerticalIcon className="size-6" />
                  </div>
                }
              >
                <div className="flex flex-col py-2 -mt-4 bg-white rounded-lg shadow-lg w-fit">
                  <button
                    className="flex items-center gap-2 px-4 py-3 whitespace-nowrap hover:bg-gray-50"
                    type="button"
                    onClick={() => duplicateBody(body)}
                  >
                    <GoRepoForked className="size-5" /> Créer une nouvelle
                    version
                  </button>
                  <button
                    type="button"
                    className="flex gap-2 px-4 py-3 text-start hover:bg-gray-50"
                  >
                    {targetsSelectorRenderer(
                      body.provisional_id,
                      <div className="flex gap-2 text-start whitespace-nowrap">
                        <UsersIcon className="size-5 " />
                        {intl.formatMessage({
                          id: "body_editor.select_social_networks",
                          defaultMessage: "Sélection des réseaux sociaux",
                        })}
                      </div>,
                    )}
                  </button>
                  {!isLast && (
                    <button
                      className="flex gap-2 px-4 py-3 text-start hover:bg-gray-50"
                      type="button"
                      onClick={() => deleteBody(body)}
                    >
                      <TrashIcon className="size-5 " /> Supprimer cette version
                    </button>
                  )}
                </div>
              </CustomPopover>
              <div className="hidden lg:flex">
                {targetsSelectorRenderer(body.provisional_id)}
              </div>

              <Tooltip
                text={intl.formatMessage({
                  id: "body_editor.create_version",
                  defaultMessage:
                    "Créer une version différente d'un réseau à l'autre",
                })}
                position="bottomleft"
              >
                <button
                  className="hidden gap-2 ml-2 btn-neutral lg:flex"
                  type="button"
                  onClick={() => duplicateBody(body)}
                >
                  <GoRepoForked className="size-5" />{" "}
                  {intl.formatMessage({
                    id: "body_editor.version",
                    defaultMessage: "Version",
                  })}
                </button>
              </Tooltip>
              {!isLast && (
                <Tooltip
                  text={intl.formatMessage({
                    id: "body_editor.delete_version",
                    defaultMessage: "Supprimer cette version",
                  })}
                  position="bottomright"
                >
                  <button
                    className="hidden gap-2 ml-2 btn-neutral lg:flex"
                    type="button"
                    onClick={() => deleteBody(body)}
                  >
                    <TrashIcon className="size-5" />{" "}
                    {intl.formatMessage({
                      id: "body_editor.delete",
                      defaultMessage: "Supprimer",
                    })}
                  </button>
                </Tooltip>
              )}
            </div>

            {/* Toogle for isStory input */}
            {isInsta && (
              <div className="stonly-post-format flex gap-1 h-fit rounded-full bg-gray-950/5 p-1">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`bodies[${index}][post_type]`}
                    value="post"
                    checked={body.post_type === "post"}
                    onChange={() => updatePostType("post")}
                    className="peer hidden"
                  />
                  <span className="group flex items-center rounded-full px-2 py-0.5 text-gray-500 peer-checked:text-gray-700 text-xs font-medium peer-checked:bg-white peer-checked:ring-1 peer-checked:ring-gray-950/5">
                    Post
                  </span>
                </label>

                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`bodies[${index}][post_type]`}
                    value="story"
                    checked={body.post_type === "story"}
                    onChange={() => updatePostType("story")}
                    className="peer hidden"
                  />
                  <span className="group flex items-center rounded-full px-2 py-0.5 text-gray-500 peer-checked:text-gray-700 text-xs font-medium peer-checked:bg-white peer-checked:ring-1 peer-checked:ring-gray-950/5">
                    Story
                  </span>
                </label>

                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`bodies[${index}][post_type]`}
                    value="post_and_story"
                    checked={body.post_type === "post_and_story"}
                    onChange={() => updatePostType("post_and_story")}
                    className="peer hidden"
                  />
                  <span className="group flex items-center rounded-full px-2 py-0.5 text-gray-500 peer-checked:text-gray-700 text-xs font-medium peer-checked:bg-white peer-checked:ring-1 peer-checked:ring-gray-950/5">
                    {intl.formatMessage({
                      id: "body_editor.post_type_double",
                      defaultMessage: "Double",
                    })}
                  </span>
                </label>
              </div>
            )}


            <div className="flex items-start gap-1 flex-grow md:flex-grow-0">
              <button
                type="button"
                onClick={() => applySelectedStyle("bold")}
                className="flex items-center justify-center w-6 text-lg font-bold border rounded-md cursor-pointer hover:bg-gray-50"
              >
                B
              </button>
              <button
                type="button"
                onClick={() => applySelectedStyle("italic")}
                className="flex items-center justify-center w-6 text-lg italic border rounded-md cursor-pointer hover:bg-gray-50"
              >
                I
              </button>
            </div>
          </div>
        </div>
        <div className="relative flex flex-col w-full my-2">
          <div
            className="caret-black w-full max-w-full min-w-full max-h-full min-h-full h-full absolute inset-0 rounded-md whitespace-pre-wrap overflow-y-auto break-words no-scrollbar"
            // On reproduit le style de la div du textarea
            ref={fakeInputRef}
            style={{
              zIndex: -1,
              fontSize: "1rem",
              lineHeight: "1.5rem",
              padding: ".5rem .75rem",
            }}
          >
            <div
              className="min-w-full min-h-full"
              style={{
                ...(inputRef?.current?.scrollHeight && {
                  height: inputRef?.current?.scrollHeight,
                }),
                ...(inputRef?.current?.scrollWidth && {
                  width: inputRef?.current?.scrollWidth,
                }),
                width: inputRef?.current?.scrollHeight,
              }}
            >
              {generateMentionOrVariableSpans(
                normalizeMentionsAndVariables(provisionalMessage || ""),
              )}
            </div>
          </div>

          <textarea
            ref={inputRef}
            placeholder={intl.formatMessage({
              id: "body_editor.placeholder",
              defaultMessage: "Saisissez votre texte ici",
            })}
            className="caret-black text-transparent bg-transparent w-full h-full placeholder-gray-400 border-0 focus:ring-0 whitespace-pre-wrap break-words"
            //required
            rows={10}
            value={provisionalMessage || ""}
            onChange={handleInput}
            style={{
              fontSize: "1rem",
              lineHeight: "1.5rem",
              padding: ".5rem .75rem",
            }}
            onScroll={(e) => {
              fakeInputRef.current?.scrollTo(
                e.target.scrollLeft,
                e.target.scrollTop,
              );
            }}
          />
        </div>
        {/* hidden inputs */}
        <textarea
          name={`bodies[${index}][message]`}
          className="hidden"
          value={body.message ?? ""}
          readOnly
        />
        <input
          type="hidden"
          name={`bodies[${index}][id]`}
          value={body.id || ""}
        />
        <input
          type="hidden"
          name={`bodies[${index}][provisional_id]`}
          value={body.provisional_id}
        />
        {/* Links Displaying */}
        {body?.link_url && (
          <div className="relative flex flex-col gap-2 px-3 py-4 mt-1 mb-4 bg-gray-100  group max-w-80 rounded-b-md">
            <button
              type="button"
              className={`flex md:hidden group-hover:flex absolute -top-2 -right-2 justify-center items-center border w-6 h-6 border-red-400 text-xs text-red-400 bg-white rounded-full cursor-pointer !hover:bg-red-400 !hover:text-white hover:bg-red-400`}
              onClick={removeLink}
            >
              <XMarkIcon className="w-4 h-4 text-red-500 hover:text-white" />
            </button>
            <p className="font-bold text-gray-600 truncate">
              {body?.link_url && displayableUrl(body.link_url)}
            </p>
            <p className="text-sm text-gray-800 truncate">
              {body?.link_description || ""}
            </p>
            {bodyCta?.value && body?.link_cta && (
              <span className="absolute bottom-2 right-2 btn-neutral !px-2 !py-1 flex text-sm">
                {bodyCta.label}
              </span>
            )}
          </div>
        )}
        {/* Editing Features */}
        <div className="relative flex flex-wrap justify-between gap-5 items-end">
          <div className="relative flex items-center justify-start gap-2 sm:gap-3">
            {/* Body Link button */}
            <BodyLink body={body} updateBody={updateBody} />
            <input
              type="hidden"
              name={`bodies[${index}][link_url]`}
              value={body?.link_url ?? ""}
            />
            <input
              type="hidden"
              name={`bodies[${index}][link_description]`}
              value={body?.link_description ?? ""}
            />
            <input
              type="hidden"
              name={`bodies[${index}][link_cta]`}
              value={body?.link_cta ?? ""}
            />

            <Tooltip
              text={intl.formatMessage({
                id: "body_editor.insert_emojis",
                defaultMessage: "Insérer des émojis dans le texte",
              })}
              position="bottom"
            >
              <button
                type="button"
                onClick={() => setEmojiSelectorOpen(true)}
                className="btn-small-round"
              >
                <FaceSmileIcon className="size-7" />
              </button>
            </Tooltip>

            <EmojiPickerCustom
              isOpen={emojiSelectorOpen}
              setIsOpen={setEmojiSelectorOpen}
              containerClassName="absolute -bottom-12 origin-bottom border rounded !z-50 h-96 max-h-96 min-h-96 overflow-auto"
              perLine={9}
              maxFrequentRows={1}
              previewPosition="bottom"
              navPosition="top"
              searchPosition="top"
              theme="light"
              onEmojiSelect={onEmojiSelect}
            />
            <div className="relative">
              <Tooltip
                text={intl.formatMessage({
                  id: "body_editor.ai_tooltip",
                  defaultMessage:
                    "Génération de contenu ou d'image grâce à l'intelligence artificielle",
                })}
                position="bottom"
              >
                <button
                  type="button"
                  className="relative btn-small-round"
                  onClick={() => setShowAiModal(!showAiModal)}
                >
                  IA
                </button>
              </Tooltip>
              <Modal open={showAiModal} setOpen={setShowAiModal}>
                <div className="relative p-12 my-auto bg-white rounded-md w-[44rem] max-w-[90vw] overflow-y-auto">
                  <CustomCloseButton
                    open={showAiModal}
                    setOpen={setShowAiModal}
                  />
                  {/* Onglets : bascule texte / image (ne change pas la vue) */}
                  <div className="flex gap-1 mb-6 w-fit rounded-full bg-gray-950/5 p-1">
                    <button
                      type="button"
                      onClick={() => setAiTab("text")}
                      className={`flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${aiTab === "text"
                        ? "bg-white text-gray-700 ring-1 ring-gray-950/5"
                        : "text-gray-500"
                        }`}
                    >
                      {intl.formatMessage({
                        id: "body_editor.ai_tab_text",
                        defaultMessage: "Texte",
                      })}
                    </button>
                    {aiImageEnabled && (
                    <button
                      type="button"
                      onClick={() => setAiTab("image")}
                      className={`flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${aiTab === "image"
                        ? "bg-white text-gray-700 ring-1 ring-gray-950/5"
                        : "text-gray-500"
                        }`}
                    >
                      <SparklesIcon className="size-4" />
                      {intl.formatMessage({
                        id: "body_editor.ai_tab_image",
                        defaultMessage: "Image",
                      })}
                    </button>
                    )}
                  </div>

                  {aiTab === "text" || !aiImageEnabled ? (
                    <ModalInput
                      title={intl.formatMessage({
                        id: "body_editor.ai_title",
                        defaultMessage: "Générateur de contenu par IA",
                      })}
                      value={prompt}
                      message={intl.formatMessage({
                        id: "body_editor.ai_message",
                        defaultMessage:
                          "Entrez le sujet de votre article et détaillez le contenu à créer, ou indiquez comment modifier le texte existant.",
                      })}
                      placeholder={intl.formatMessage({
                        id: "body_editor.ai_placeholder",
                        defaultMessage:
                          "Exemple: Rédigez un article sur les tendances immo 2025",
                      })}
                      setValue={setPrompt}
                      loading={gptIsWriting}
                      error={gptError}
                      Submit={() => fetchChatGPTResponse(prompt, body.message)}
                      multiline
                    />
                  ) : (
                    <ModalInput
                      title={intl.formatMessage({
                        id: "body_editor.ai_image_title",
                        defaultMessage: "Générateur d'image par IA",
                      })}
                      value={imagePrompt}
                      message={intl.formatMessage({
                        id: "body_editor.ai_image_message",
                        defaultMessage:
                          "Décrivez l'image que vous souhaitez générer. Le logo et les informations de votre entreprise sont automatiquement pris en compte.",
                      })}
                      placeholder={intl.formatMessage({
                        id: "body_editor.ai_image_placeholder",
                        defaultMessage:
                          "Exemple: Une photo lumineuse d'un salon moderne avec une plante verte",
                      })}
                      setValue={setImagePrompt}
                      loading={aiImageLoading}
                      error={aiImageError}
                      Submit={() => generateAiImage(imagePrompt)}
                      multiline
                    />
                  )}
                </div>
              </Modal>
            </div>
            <div className="relative">
              <VariableSelector variables={variables} insert={insert} />
            </div>
            <div>
              <Tooltip
                text={intl.formatMessage({
                  id: "body_editor.smartlink_tooltip",
                  defaultMessage: "Construire un contenu à partir d'une URL",
                })}
                position="bottomright"
              >
                <button
                  type="button"
                  className="btn-small-round"
                  onClick={toggleSmartlinkModal}
                >
                  <CloudArrowDownIcon className="size-6" />
                </button>
              </Tooltip>
              <Modal open={showSmartLinkModal} setOpen={setShowSmartLinkModal}>
                <div className="relative p-12 my-auto bg-white rounded-md">
                  <CustomCloseButton
                    open={showSmartLinkModal}
                    setOpen={setShowSmartLinkModal}
                  />
                  <ModalInput
                    title={intl.formatMessage({
                      id: "body_editor.smartlink_title",
                      defaultMessage: "Smartlink",
                    })}
                    value={smartLinkUrl}
                    message={intl.formatMessage({
                      id: "body_editor.smartlink_message",
                      defaultMessage:
                        "Entrez l'url d'un article et nous écrirons pour vous une accroche, cela remplacera le contenu actuel.",
                    })}
                    placeholder="https://example.com/article"
                    setValue={setSmartLinkUrl}
                    loading={smartLinkLoading}
                    error={smartLinkError}
                    Submit={replaceBodyWithRemote}
                  />
                </div>
              </Modal>
            </div>
            {/* Le rendu du selecteur de mention */}
            {mentionSelectorRenderer?.(body, insert)}
          </div>
          {characterLimit !== undefined && (
            <CharacterCounter count={body.message?.length ?? 0} limit={characterLimit} />
          )}
        </div>
        {/* Le visuel du post, après les outils du texte : deux blocs, deux langages. */}
        {body.composition_id && (
          /* Les médias du body sont une composition persistée, rendue serveur-side dans un
             îlot turbo-frame que l'îlot PATCHe directement. Le serveur sert un BROUILLON
             orphelin (fork-à-l'ouverture) : composition_id repart en hidden pour que le save
             l'adopte et le lie — rien ne change avant Enregistrer. Plusieurs versions peuvent
             pointer la même compo (visuel partagé) : chaque îlot a son id de frame (le
             serveur le reprend), tous suivent le même signal. */
          <>
            <input
              type="hidden"
              name={`bodies[${index}][composition_id]`}
              value={body.composition_id}
            />
            {sharedWithCount > 0 && (
              <div className="flex items-center gap-3 mb-2 text-sm text-gray-600">
                <span>
                  {intl.formatMessage(
                    {
                      id: "body_editor.shared_composition",
                      defaultMessage:
                        "Visuel partagé avec {count, plural, one {# autre version} other {# autres versions}}",
                    },
                    { count: sharedWithCount },
                  )}
                </span>
                <button
                  type="button"
                  className="underline hover:text-gray-900"
                  onClick={divergeComposition}
                >
                  {intl.formatMessage({
                    id: "body_editor.diverge_composition",
                    defaultMessage: "Personnaliser pour cette version",
                  })}
                </button>
              </div>
            )}
            <div className="flex w-full mt-4 pt-4 border-t border-surface_200">
              {React.createElement("turbo-frame", {
                id: `strip_composition_${body.composition_id}_body_${body.provisional_id}`,
                src: `/compositions/${body.composition_id}?variant=strip`,
                class: "block w-full",
                // Turbo lit `refresh` sur l'élément DOM (celui-ci), pas sur la réponse : sans
                // lui, reload() remplace tout le sous-arbre au lieu de morpher (flicker).
                refresh: "morph",
                // Le broadcast-signal (reload_frame) vise ce sélecteur — sur l'élément vivant,
                // Turbo ne copiant pas les attributs du frame de réponse.
                "data-composition-signal": body.composition_id,
              })}
            </div>
          </>
        )}
      </div>
    );
  },
);

export default BodyEditor;
